import React, { useRef } from "react";
import { Th } from "./th";
import { Td } from "./td";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { usePinnedColumnWidths } from "./usePinnedColumnWidths";
export function VirtualizedTable({ tableOptions, data, columns, estimateItemSize, isSelected, rowClassName }) {
    const tableContainerRef = useRef(null);
    // TanStack Table is a known React Compiler incompatibility in this repo.
    // eslint-disable-next-line react-hooks/incompatible-library
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });
    const { rows } = table.getRowModel();
    /**
     * Rows are measured, not estimated. `estimateItemSize` is only a first
     * guess — a wrapping cell makes a row taller than that. Without
     * `measureElement`, `getTotalSize()` (which sizes the spacer rows) never
     * matches actual content height, and since the gap depends on which rows
     * are mounted, total size shifts on every scroll tick. At the bottom the
     * scroll offset is pinned to the max, so each shrink gets clamped: the
     * view jumps, height gets restored, and the list oscillates instead of
     * settling.
     */
    const rowVirtualize = useVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => estimateItemSize,
        // Measurements keyed by row, not by index: after a sort/filter a row that stays mounted is never
        // re-measured (stable ref), so an index key would give its old slot's height to the new occupant.
        getItemKey: (index) => rows[index].id,
        overscan: 5,
    });
    const virtualItems = rowVirtualize.getVirtualItems();
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const paddingBottom = virtualItems.length > 0 ? rowVirtualize.getTotalSize() - virtualItems[virtualItems.length - 1].end : 0;
    const leafColumns = table.getVisibleLeafColumns();
    const columnCount = leafColumns.length;
    const columnKey = leafColumns.map((column) => column.id).join("|");
    // The virtualizer mounts no row until it has read its scroll element, so the first pass
    // measures the header alone: re-measure once rows are on screen.
    const { headRowRef, columnWidths } = usePinnedColumnWidths(tableContainerRef, columnKey, data.length, virtualItems.length > 0);
    return (<section className="flex flex-col h-full min-h-0 max-h-full">
            {!!tableOptions && <div>{tableOptions}</div>}

            <div ref={tableContainerRef} className="grow min-h-0 overflow-auto" style={{ overflowAnchor: "none" }}>
                <table className="w-full border-separate border-spacing-0" style={columnWidths ? { tableLayout: "fixed" } : undefined}>
                    {columnWidths && (<colgroup>
                            {columnWidths.map((width, index) => (<col key={index} style={{ width: `${width}px` }}/>))}
                        </colgroup>)}

                    <thead className="sticky top-0 z-10 text-gray-600">
                        {table.getHeaderGroups().map((headerGroup, groupIndex) => (<tr key={headerGroup.id} ref={groupIndex === 0 ? headRowRef : undefined}>
                                {headerGroup.headers.map((header, headerIndex) => (<Th key={header.id} className={`
                                            chrome border-b border-line
                                            ${headerIndex === 0 ? "rounded-tl-xl" : ""}
                                            ${headerIndex === headerGroup.headers.length - 1 ? "rounded-tr-xl" : ""}
                                        `}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </Th>))}
                            </tr>))}
                    </thead>

                    <tbody>
                        {paddingTop > 0 && (<tr>
                                <td colSpan={columnCount} style={{ height: `${paddingTop}px` }}/>
                            </tr>)}

                        {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (<tr key={row.id} data-index={virtualRow.index} ref={rowVirtualize.measureElement} className={`
                                        [&>td]:border-b [&>td]:border-line
                                        ${rowClassName?.(row.original) || "text-gray-700 hover:bg-sunken"}
                                        ${!isSelected ? "" : isSelected(row?.original?.id) ? "bg-selected" : ""}
                                    `}>
                                    {row.getVisibleCells().map((cell) => (<Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>))}
                                </tr>);
        })}

                        {paddingBottom > 0 && (<tr>
                                <td colSpan={columnCount} style={{ height: `${paddingBottom}px` }}/>
                            </tr>)}
                    </tbody>
                </table>
            </div>
        </section>);
}
//# sourceMappingURL=virtualizedTable.jsx.map